import { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { ObservableField } from 'shared/utils/observer-field';

const PING_INTERVAL_MS = 10_000;
const PONG_TIMEOUT_MS = 5_000;
const CHANNEL_NAME = 'overlay_ping_pong';

/**
 * Uses a Supabase Realtime broadcast channel to measure round-trip latency
 * (Phoenix-level ping/pong). Sends a "ping" every PING_INTERVAL_MS and waits
 * up to PONG_TIMEOUT_MS for a "pong" echo from the server side.
 *
 * latencyMs — last measured RTT in ms, null when no measurement yet.
 * isAlive   — false when the last ping timed out without a pong.
 */
export class RealtimePingPong {
  latencyMs: ObservableField<number | null> = new ObservableField<number | null>(null);
  isAlive: ObservableField<boolean> = new ObservableField<boolean>(true);

  private channel?: RealtimeChannel;
  private pingTimer?: ReturnType<typeof setInterval>;
  private pongTimer?: ReturnType<typeof setTimeout>;
  private pendingPingId?: string;
  private pendingSentAt?: number;

  constructor(private supabase: SupabaseClient) {}

  public start() {
    this.channel = this.supabase
      .channel(CHANNEL_NAME, { config: { broadcast: { self: true } } })
      .on('broadcast', { event: 'pong' }, ({ payload }) => {
        if (payload?.id !== this.pendingPingId) return;

        clearTimeout(this.pongTimer);
        this.pongTimer = undefined;

        if (this.pendingSentAt !== undefined) {
          this.latencyMs.value = Date.now() - this.pendingSentAt;
        }
        this.isAlive.value = true;
        this.pendingPingId = undefined;
        this.pendingSentAt = undefined;
      })
      .on('broadcast', { event: 'ping' }, ({ payload }) => {
        // Echo back as pong so the self-broadcast loop completes
        this.channel?.send({ type: 'broadcast', event: 'pong', payload });
      })
      .subscribe((status) => {
        console.debug({ event: 'ping-pong channel', status });
        if (status === 'SUBSCRIBED') {
          this.schedulePing();
        }
      });
  }

  public stop() {
    clearInterval(this.pingTimer);
    clearTimeout(this.pongTimer);
    this.pingTimer = undefined;
    this.pongTimer = undefined;
    this.pendingPingId = undefined;
    this.pendingSentAt = undefined;

    if (this.channel) {
      this.channel.unsubscribe();
      this.channel = undefined;
    }
  }

  private schedulePing() {
    this.pingTimer = setInterval(() => this.sendPing(), PING_INTERVAL_MS);
    // Send the first ping immediately
    this.sendPing();
  }

  private sendPing() {
    if (!this.channel) return;

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    this.pendingPingId = id;
    this.pendingSentAt = Date.now();

    this.channel.send({ type: 'broadcast', event: 'ping', payload: { id } });

    this.pongTimer = setTimeout(() => {
      if (this.pendingPingId === id) {
        console.warn({ event: 'ping-pong timeout', id });
        this.isAlive.value = false;
        this.pendingPingId = undefined;
        this.pendingSentAt = undefined;
      }
    }, PONG_TIMEOUT_MS);
  }
}
